import type { ConnectError } from '@connectrpc/connect'
import {
  DemandForecastConfirmErrorReason,
  DemandForecastConfirmErrorReasonSchema,
  DemandForecastConfirmRecordPreviewErrorReason,
  DemandForecastConfirmRecordPreviewErrorReasonSchema,
  DemandForecastGetErrorReason,
  DemandForecastGetErrorReasonSchema,
  DemandForecastSaveErrorReason,
  DemandForecastSaveErrorReasonSchema,
  DemandForecastSimpleForecastEntryDefaultsErrorReason,
  DemandForecastSimpleForecastEntryDefaultsErrorReasonSchema,
  DemandForecastSimpleForecastPreviewErrorReason,
  DemandForecastSimpleForecastPreviewErrorReasonSchema,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import type { TFunction } from 'i18next'

import {
  type AppErrorInfoReasonEntry,
  readAppErrorInfo,
  resolveAppErrorInfoMessage,
} from '~/libs/appErrorInfo'
import {
  LIMIT_MESSAGE_KEYS,
  limitMessageParams,
} from '~/pages/DemandForecastDaily/models/slotValueLimit'

/**
 * 確定の registry entry。`limitMetadata` は、文言が backend の metadata (コマ番号・
 * 整形しない値) を対象日と一緒に整形して埋め込む reason (AC-DFC-EDIT-004)。
 */
type ConfirmReasonEntry = AppErrorInfoReasonEntry & { limitMetadata?: true }

/**
 * `DemandForecastConfirmErrorReason` (backend `demandforecast` domain) →
 * i18n key registry for the 需要想定確定ダイアログ。
 *
 * 全 entry が operation-scoped (ダイアログ上部の Alert) — 確定 request は
 * 48 コマ値を持たず (backend が保存済み draft から読む)、どの reason も
 * 単一入力欄に 1:1 で帰属しないため。
 *
 * spec: `docs/specs/demand/demand-forecast-common.md` AC-DFC-006d
 * (「なぜ確定できないか」を原因ごとに分岐して表示する / issue #5433)。
 * `UNSPECIFIED` は専用文言を持たず、確定できない原因を backend が分類できな
 * かった場合と同じ最終フォールバック文言 (AC-DFC-006d 末尾の「想定外の原因」)
 * へ落とす。
 */
const CONFIRM_REASON_KEYS: Record<
  DemandForecastConfirmErrorReason,
  ConfirmReasonEntry | undefined
> = {
  [DemandForecastConfirmErrorReason.UNSPECIFIED]: undefined,
  [DemandForecastConfirmErrorReason.SLOT_NOT_FILLED]: {
    key: 'page:demandForecastDaily.confirm.serverError.reason.slotNotFilled',
  },
  [DemandForecastConfirmErrorReason.DRAFT_NOT_SAVED]: {
    key: 'page:demandForecastDaily.confirm.serverError.reason.draftNotSaved',
  },
  [DemandForecastConfirmErrorReason.CASE_NOT_EDITABLE]: {
    key: 'page:demandForecastDaily.confirm.serverError.reason.caseNotEditable',
  },
  // AC-DFC-006d / AC-DFC-EDIT-004: 保存前の画面判定と同じ文言 (valueLimit.*)。
  [DemandForecastConfirmErrorReason.SLOT_VALUE_OVER_LIMIT]: {
    key: LIMIT_MESSAGE_KEYS.slot,
    limitMetadata: true,
  },
  [DemandForecastConfirmErrorReason.BG_TOTAL_OVER_LIMIT]: {
    key: LIMIT_MESSAGE_KEYS.bgTotal,
    limitMetadata: true,
  },
  [DemandForecastConfirmErrorReason.UNATTRIBUTED_REMAINING]: {
    key: 'page:demandForecastDaily.confirm.serverError.reason.unattributedRemaining',
  },
}

const GET_REASON_KEYS: Record<
  DemandForecastGetErrorReason,
  AppErrorInfoReasonEntry | undefined
> = {
  [DemandForecastGetErrorReason.UNSPECIFIED]: undefined,
  [DemandForecastGetErrorReason.CLIENT_UPDATE_REQUIRED]: {
    key: 'page:demandForecastDaily.save.serverError.reason.clientUpdateRequired',
  },
}

export function resolveGetDetailedErrorMessage(
  err: ConnectError,
  t: TFunction,
): string | undefined {
  return resolveAppErrorInfoMessage(
    err,
    DemandForecastGetErrorReasonSchema,
    GET_REASON_KEYS,
    t,
  )?.message
}

const SIMPLE_ENTRY_DEFAULTS_REASON_KEYS: Record<
  DemandForecastSimpleForecastEntryDefaultsErrorReason,
  AppErrorInfoReasonEntry | undefined
> = {
  [DemandForecastSimpleForecastEntryDefaultsErrorReason.UNSPECIFIED]: undefined,
  [DemandForecastSimpleForecastEntryDefaultsErrorReason.UPPER_SECTION_COPY_SOURCE_NOT_FOUND]:
    {
      key: 'page:demandForecastDaily.simpleForecast.serverError.reason.upperSectionCopySourceNotFound',
    },
  [DemandForecastSimpleForecastEntryDefaultsErrorReason.INSUFFICIENT_SOURCE_CANDIDATES_SINGULAR_CATEGORY]:
    {
      key: 'page:demandForecastDaily.simpleForecast.serverError.reason.insufficientSourceCandidatesSingularCategory',
    },
  [DemandForecastSimpleForecastEntryDefaultsErrorReason.INSUFFICIENT_SOURCE_CANDIDATES_AFTER_SINGULAR_EXCLUSION]:
    {
      key: 'page:demandForecastDaily.simpleForecast.serverError.reason.insufficientSourceCandidatesAfterSingularExclusion',
    },
}

/** Resolves a defaults-query reason, or leaves the code-based fallback to its caller. */
export function resolveEntryDefaultsDetailedErrorMessage(
  err: ConnectError,
  t: TFunction,
): string | undefined {
  return resolveAppErrorInfoMessage(
    err,
    DemandForecastSimpleForecastEntryDefaultsErrorReasonSchema,
    SIMPLE_ENTRY_DEFAULTS_REASON_KEYS,
    t,
  )?.message
}

/**
 * 確定失敗 error から原因別の文言を解決する。`google.rpc.ErrorInfo` detail が
 * 無い / reason が未知 / registry entry が `undefined` の場合は `undefined` を
 * 返し、呼び出し元の code ベース fallback に委ねる。
 *
 * `limitMetadata` の reason (AC-DFC-EDIT-004) は metadata のコマ番号・値 (整形しない)
 * を、画面が持つ対象日と一緒に桁区切り・時刻表記へ整形して文言へ埋め込む。
 * metadata が欠けている / 数値でない時は文言を作らず `undefined` にする。
 */
export function resolveConfirmDetailedErrorMessage(
  err: ConnectError,
  t: TFunction,
  targetDateLabel: string,
): string | undefined {
  const info = readAppErrorInfo(err, DemandForecastConfirmErrorReasonSchema)
  if (info === undefined) return undefined
  let interpolation: Record<string, unknown> | undefined
  if (CONFIRM_REASON_KEYS[info.reason]?.limitMetadata === true) {
    const koma = Number(info.metadata.koma)
    const value = info.metadata.value
    if (
      !Number.isInteger(koma) ||
      value === undefined ||
      !Number.isFinite(Number(value))
    ) {
      return undefined
    }
    interpolation = limitMessageParams({ koma, value }, targetDateLabel)
  }
  return resolveAppErrorInfoMessage(
    err,
    DemandForecastConfirmErrorReasonSchema,
    CONFIRM_REASON_KEYS,
    t,
    interpolation,
  )?.message
}

/**
 * `DemandForecastSaveErrorReason` (backend `demandforecast` domain) →
 * i18n key registry for SaveDemandForecastDraft. Operation-scoped: the
 * readonly rejection is a case-level
 * precondition, not attributable to a single koma / field.
 */
const SAVE_REASON_KEYS: Record<
  DemandForecastSaveErrorReason,
  AppErrorInfoReasonEntry | undefined
> = {
  [DemandForecastSaveErrorReason.UNSPECIFIED]: undefined,
  [DemandForecastSaveErrorReason.CASE_NOT_EDITABLE]: {
    key: 'page:demandForecastDaily.save.serverError.reason.caseNotEditable',
  },
  [DemandForecastSaveErrorReason.CLIENT_UPDATE_REQUIRED]: {
    key: 'page:demandForecastDaily.save.serverError.reason.clientUpdateRequired',
  },
}

/**
 * Save 失敗 error から原因別の文言を解決する。fallback 契約は
 * resolveConfirmDetailedErrorMessage と同じ (ErrorInfo 無し / reason 未知 /
 * registry entry undefined はいずれも呼び出し元の code ベース fallback へ委ねる)。
 */
export function resolveSaveDetailedErrorMessage(
  err: ConnectError,
  t: TFunction,
): string | undefined {
  return resolveAppErrorInfoMessage(
    err,
    DemandForecastSaveErrorReasonSchema,
    SAVE_REASON_KEYS,
    t,
  )?.message
}

/**
 * `DemandForecastSimpleForecastPreviewErrorReason` (backend `demandforecast`
 * domain) → i18n key registry for PreviewSimpleForecast (§7 簡易予測プレビュー)。
 * ①②③ の 3 reason は spec の逐語文言 — AC-DFM-013 / AC-DFM-008 /
 * AC-DFM-006a-1 — をそのまま i18n key へ移す (旧実装の method 手動分岐を廃止,
 * Design Note demand-forecast-non-confirm-reason-registry.md Decision #2)。
 * `insufficientActualData` は spec 文言が手法名を埋め込むため
 * `resolveSimplePreviewDetailedErrorMessage` の呼び出し元が `methodName` を渡す
 * (未使用 key の placeholder は i18next が無視するので他 entry には影響しない)。
 * 全 entry が operation-scoped (`field` なし = 取込確認ダイアログ上部の Alert
 * またはトースト)。
 */
const SIMPLE_PREVIEW_REASON_KEYS: Record<
  DemandForecastSimpleForecastPreviewErrorReason,
  AppErrorInfoReasonEntry | undefined
> = {
  [DemandForecastSimpleForecastPreviewErrorReason.UNSPECIFIED]: undefined,
  [DemandForecastSimpleForecastPreviewErrorReason.CASE_NOT_EDITABLE]: {
    key: 'page:demandForecastDaily.simpleForecast.serverError.reason.caseNotEditable',
  },
  [DemandForecastSimpleForecastPreviewErrorReason.UPPER_SECTION_COPY_SOURCE_NOT_FOUND]:
    {
      key: 'page:demandForecastDaily.simpleForecast.serverError.reason.upperSectionCopySourceNotFound',
    },
  [DemandForecastSimpleForecastPreviewErrorReason.INSUFFICIENT_ACTUAL_DATA]: {
    key: 'page:demandForecastDaily.simpleForecast.serverError.reason.insufficientActualData',
  },
  [DemandForecastSimpleForecastPreviewErrorReason.INSUFFICIENT_SOURCE_CANDIDATES_SINGULAR_CATEGORY]:
    {
      key: 'page:demandForecastDaily.simpleForecast.serverError.reason.insufficientSourceCandidatesSingularCategory',
    },
  [DemandForecastSimpleForecastPreviewErrorReason.INSUFFICIENT_SOURCE_CANDIDATES_AFTER_SINGULAR_EXCLUSION]:
    {
      key: 'page:demandForecastDaily.simpleForecast.serverError.reason.insufficientSourceCandidatesAfterSingularExclusion',
    },
  [DemandForecastSimpleForecastPreviewErrorReason.CLIENT_UPDATE_REQUIRED]: {
    key: 'page:demandForecastDaily.save.serverError.reason.clientUpdateRequired',
  },
}

/**
 * PreviewSimpleForecast 失敗 error から原因別の文言を解決する。methodName は
 * `INSUFFICIENT_ACTUAL_DATA` の spec 文言 (AC-DFM-008「<手法名> に必要な実績
 * データが不足しています」) の埋め込みにのみ使う。fallback 契約は
 * resolveConfirmDetailedErrorMessage と同じ。
 */
export function resolveSimplePreviewDetailedErrorMessage(
  err: ConnectError,
  t: TFunction,
  methodName: string,
): string | undefined {
  return resolveAppErrorInfoMessage(
    err,
    DemandForecastSimpleForecastPreviewErrorReasonSchema,
    SIMPLE_PREVIEW_REASON_KEYS,
    t,
    { methodName },
  )?.message
}

/**
 * `DemandForecastConfirmRecordPreviewErrorReason` (backend `demandforecast`
 * domain) → i18n key registry for PreviewConfirmRecord (§5 確定履歴の参照列
 * プレビュー)。`NO_MATCHING_RETAILERS` の文言は spec 未定義 (AC-DFD-032 は発生
 * 条件のみ gate) のため EN 起草 (Note AC セクション)。全 entry が
 * operation-scoped。
 */
const CONFIRM_RECORD_PREVIEW_REASON_KEYS: Record<
  DemandForecastConfirmRecordPreviewErrorReason,
  AppErrorInfoReasonEntry | undefined
> = {
  [DemandForecastConfirmRecordPreviewErrorReason.UNSPECIFIED]: undefined,
  [DemandForecastConfirmRecordPreviewErrorReason.CASE_NOT_EDITABLE]: {
    key: 'page:demandForecastDaily.confirmRecordImport.serverError.reason.caseNotEditable',
  },
  [DemandForecastConfirmRecordPreviewErrorReason.NO_MATCHING_RETAILERS]: {
    key: 'page:demandForecastDaily.confirmRecordImport.serverError.reason.noMatchingRetailers',
  },
}

/**
 * PreviewConfirmRecord 失敗 error から原因別の文言を解決する。fallback 契約は
 * resolveConfirmDetailedErrorMessage と同じ。
 */
export function resolveConfirmRecordPreviewDetailedErrorMessage(
  err: ConnectError,
  t: TFunction,
): string | undefined {
  return resolveAppErrorInfoMessage(
    err,
    DemandForecastConfirmRecordPreviewErrorReasonSchema,
    CONFIRM_RECORD_PREVIEW_REASON_KEYS,
    t,
  )?.message
}
